import React, { useState, useEffect } from "react";
import { deleteTemplate, saveTemplate } from "../api/TemplateApi";
import Editor from "../components/Editor";
import InputInline from "../components/InputInline";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import TemplateAction from "../actions/TemplateAction";
import { TemplateContexts } from "../constants/action-types";
import { useTranslation } from "react-i18next";

const visibilityModes = [
  { value: "Private", label: "Private" },
  { value: "Shared", label: "Shared" },
];

interface Template {
  id: string;
  name: string;
  promptTemplate: string;
  tags: string[];
}

const TemplateManager: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const templates = useSelector((state: any) => state.template.get("templates"));
  const pending = useSelector((state: any) => state.template.get("pending"));
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [name, setName] = useState("");
  const [promptTemplate, setPromptTemplate] = useState("");
  const [visibility, setVisibility] = useState("Private");
  const [type, setType] = useState("email");
  const [tags, setTags] = useState<string[]>([]);
  const visibilityOpt = visibilityModes.find((item) => item.value == visibility);

  // Fetch all templates
  useEffect(() => {
    dispatch(TemplateAction.getTemplates());
    dispatch(
      TemplateAction.getAttributes([
        TemplateContexts.SALE,
        TemplateContexts.SENDER,
        TemplateContexts.RECIPIENT,
      ])
    );
  }, [dispatch]);

  // Handle template selection
  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setName(template.name);
    setPromptTemplate(template.promptTemplate);
    setTags(template.tags);
  };

  // Handle form submission to create/update a template
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newTemplate = {
      id: selectedTemplate ? selectedTemplate.id : "", // Will be empty when creating new
      name,
      promptTemplate,
      tags,
    };

    if (selectedTemplate) {
      saveTemplate(newTemplate)
        .then(() => {
          setSelectedTemplate(null); // Clear selection
        })
        .catch((err) => console.error("Failed to update template", err));
    } else {
      saveTemplate(newTemplate)
        .then(() => {})
        .catch((err) => console.error("Failed to create template", err));
    }

    // Clear form fields
    setName("");
    setPromptTemplate("");
    setTags([]);
  };

  // Handle deletion
  const handleDelete = (id: string) => {
    deleteTemplate(id)
      .then(() => {})
      .catch((err) => console.error("Failed to delete template", err));
  };

  const templateTypes = [
    { id: "email", title: "Email" },
    { id: "comment", title: "Comment" },
  ];

  const renderForm = () => {
    return (
      <div>
        <h2 className="text-2xl">{selectedTemplate ? t("Edit Template") : t("Create Template")}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block">
              {t("Template Name")}
            </label>
            <InputInline
              border={true}
              value={name}
              placeholder={t("e.g Email template")}
              onFinish={setName}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="name" className="block">
              {t("Type")}
            </label>
            <div className="space-y-6 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
              {templateTypes.map((templateType) => (
                <div key={templateType.id} className="flex items-center">
                  <input
                    checked={templateType.id == type}
                    id={templateType.id}
                    type="radio"
                    onChange={() => {
                      setType(templateType.id);
                    }}
                    className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-blue-600 checked:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden"
                  />
                  <label
                    htmlFor={templateType.id}
                    className="ml-3 block text-sm/6 font-medium text-gray-900"
                  >
                    {templateType.title}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="promptTemplate" className="block">
              {t("Prompt Template")}
            </label>
            {!pending && (
              <Editor
                placeholder={t("Template content")}
                onChange={(value) => {
                  if (value && value.htmlContent) {
                    setPromptTemplate(value.htmlContent);
                  }
                }}
              />
            )}
          </div>
          <div className={"mb-4"}>
            <label className="block">{t("Visibility")}</label>
            <Select
              value={visibilityOpt}
              menuPlacement={"top"}
              onChange={(item) => {
                if (item) {
                  setVisibility(item.value);
                }
              }}
              options={visibilityModes}
            />
          </div>

          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-md">
            {selectedTemplate ? t("Update Template") : t("Create Template")}
          </button>
        </form>
      </div>
    );
  };

  const renderList = () => {
    return (
      <div className="mb-6">
        <h2 className="text-2xl">{t("Templates")}</h2>
        <ul>
          {templates.map((template) => (
            <li key={template.get("id")} className="flex justify-between items-center">
              <span>{template.get("name") || t("Blank template")}</span>
              <div>
                <button
                  onClick={() => handleSelectTemplate(template)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md mr-2"
                >
                  {t("Edit")}
                </button>
                <button
                  onClick={() => handleDelete(template.get("id"))}
                  className="bg-red-500 text-white px-4 py-2 rounded-md"
                >
                  {t("Delete")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="p-4 w-full">
      {renderList()}
      {renderForm()}
    </div>
  );
};

export default TemplateManager;
